import { cn } from "@/common/helpers/utils";
import { Input, InputVariant, inputVariants } from "@/components/ui/input";
import { useDebounce } from "@uidotdev/usehooks";
import i18next from "i18next";
import { SearchIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export interface SearchInputProps {
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  debounceMs?: number;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  showSearchIcon?: boolean;
  showClearButton?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: InputVariant;
  autoFocus?: boolean;
  maxLength?: number;
  onSearchChange?: (value: string) => void;
  onChange?: (value: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onClear?: () => void;
}

export function SearchInput({
  placeholder = i18next.t("common:Search"),
  value,
  defaultValue = "",
  debounceMs = 300,
  disabled = false,
  className,
  inputClassName,
  showSearchIcon = true,
  showClearButton = true,
  size = "md",
  variant = InputVariant.Filled,
  autoFocus = false,
  maxLength,
  onSearchChange,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  onClear,
}: SearchInputProps) {
  const { t } = useTranslation("common");
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = isControlled ? value : internalValue;
  const valueToDebounce = isControlled ? value : internalValue;
  const debouncedValue = useDebounce(valueToDebounce, debounceMs);

  useEffect(() => {
    if (!isControlled) {
      onSearchChange?.(debouncedValue);
    }
  }, [debouncedValue, onSearchChange, isControlled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (isControlled) {
        onChange?.(newValue);
      } else {
        setInternalValue(newValue);
        onChange?.(newValue);
      }
    },
    [onChange, isControlled],
  );

  const handleClear = useCallback(() => {
    if (isControlled) {
      onChange?.("");
      onSearchChange?.("");
      onClear?.();
    } else {
      setInternalValue("");
      onChange?.("");
      onSearchChange?.("");
      onClear?.();
    }
  }, [onChange, onSearchChange, onClear, isControlled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        handleClear();
      }
      onKeyDown?.(e);
    },
    [handleClear, onKeyDown],
  );

  const sizeClasses = {
    sm: "h-8 text-sm",
    md: "h-9",
    lg: "h-10 text-lg",
  };

  const containerClasses = cn(
    "relative flex items-center transition-all duration-200 backdrop-blur-sm",
    inputVariants({ variant }),
    "focus-within:ring-1 focus-within:ring-primary/20",
    sizeClasses[size],
    disabled && "opacity-50 cursor-not-allowed",
    className,
  );

  const inputClasses = cn(
    "border-0 bg-transparent dark:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground",
    "h-full",
    showSearchIcon && "pl-9",
    showClearButton && "pr-9", // Always reserve space for clear button
    inputClassName,
  );

  return (
    <div className={containerClasses}>
      {showSearchIcon && (
        <SearchIcon
          className={cn(
            "text-muted-foreground/70 absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2",
            "group-focus-within:text-primary/80 pointer-events-none z-10 transition-colors",
          )}
        />
      )}

      <Input
        type="text"
        placeholder={placeholder ?? t("Search")}
        value={currentValue}
        disabled={disabled}
        autoFocus={autoFocus}
        maxLength={maxLength}
        className={inputClasses}
        onChange={handleInputChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />

      {showClearButton && currentValue && !disabled && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClear();
          }}
          className={cn(
            "text-muted-foreground/70 absolute top-1/2 right-2 flex h-7 w-7 -translate-y-1/2 items-center justify-center",
            "hover:text-foreground hover:bg-muted/60 group/clear z-10 cursor-pointer rounded-md transition-all duration-200",
          )}
          aria-label={t("ClearSearch")}
        >
          <XIcon className="h-3.5 w-3.5 transition-transform group-hover/clear:scale-110" />
        </button>
      )}
    </div>
  );
}
